/**
 * FooterColumns.jsx — the crawlable, six-column global footer body.
 * Real <Link> text (crawlable), collapsible <details> on mobile (open by
 * default so content + links are always in the DOM for crawlers).
 * `compact` (inner pages): same links, grouped under headings that are closed by default — one tap opens a group.
 */
import React from "react";
import { Link } from "react-router-dom";
import { FOOTER_COLUMNS } from "@/lib/siteNav";

/** Under 600px the six columns collapse by default (links stay in the DOM for crawlers; summary toggles via tap/keyboard). */
function useNarrow() {
  const [narrow, setNarrow] = React.useState(() => typeof window !== "undefined" && window.matchMedia("(max-width: 599px)").matches);
  React.useEffect(() => { const mq = window.matchMedia("(max-width: 599px)"); const h = (e) => setNarrow(e.matches); mq.addEventListener("change", h); return () => mq.removeEventListener("change", h); }, []);
  return narrow;
}

export default function FooterColumns({ compact = false }) {
  const narrow = useNarrow();
  return (
    <nav
      data-testid="footer-columns"
      data-compact={compact ? "1" : "0"}
      aria-label="Site links"
      className={compact ? "flex flex-wrap gap-x-4 gap-y-0.5 text-left" : "grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-6 gap-x-6 gap-y-2 text-left"}
    >
      {FOOTER_COLUMNS.map((col) => (
        <details key={col.title} open={!compact && !narrow} className={`group ${compact ? "min-w-[9rem] flex-1 sm:flex-none" : ""}`} data-testid={`footer-col-${col.title.toLowerCase()}`}>
          <summary className={`cursor-pointer list-none min-h-[44px] py-2 font-heading text-sm font-black ${col.grownup ? "text-indigo-700" : "text-slate-900"} flex items-center ${compact ? "gap-1" : "justify-between"}`}>
            {col.title}{col.grownup && <span className="ml-1 text-[11px] font-bold uppercase tracking-wide text-indigo-500">for grown-ups</span>}
            <span className={`${compact ? "" : "sm:hidden"} text-slate-400 group-open:rotate-180 transition-transform`}>▾</span>
          </summary>
          <ul className={`mt-0.5 ${compact ? "mb-1" : ""}`}>
            {col.links.map((l) => (
              <li key={l.to + l.label}>
                <Link
                  to={l.to}
                  data-testid={`footer-link-${l.label.toLowerCase().replace(/\W+/g, "-")}`}
                  className="inline-flex items-center min-h-[44px] min-w-[44px] py-1 text-sm sm:text-xs font-semibold text-slate-600 hover:text-emerald-800 hover:underline underline-offset-2"
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </details>
      ))}
    </nav>
  );
}
